import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { AccountLayout } from "@/components/candidate/AccountLayout";
import { apiFetch } from "@/components/candidate/shared";

const inputClass =
    "w-full border border-ink/15 bg-ivory px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-forest";

export default function CandidateSettings() {
    const navigate = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [changed, setChanged] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const changePassword = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError("");
        setChanged(false);
        const res = await apiFetch("/candidate/change-password", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ current_password: current, new_password: next }),
        });
        if (res.ok) {
            setChanged(true);
            setCurrent("");
            setNext("");
        } else {
            const body = await res.json().catch(() => ({}));
            setError(typeof body.detail === "string" ? body.detail : "Could not change password.");
        }
        setBusy(false);
    };

    const deactivate = async () => {
        await apiFetch("/candidate/account", { method: "DELETE" });
        navigate("/");
    };

    return (
        <AccountLayout title="Account Settings" subtitle="Manage your sign-in, preferences and account.">
            <div className="max-w-2xl space-y-14" data-testid="settings-page">
                <section>
                    <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-forest">
                        <span className="h-px w-6 bg-champagne" aria-hidden="true" />
                        Profile & preferences
                    </p>
                    <div className="mt-5 space-y-3 text-sm">
                        <a href="/account/profile" data-testid="settings-edit-profile" className="block border border-ink/10 px-5 py-4 transition-colors hover:bg-sand">
                            Edit profile information →
                        </a>
                        <a href="/account/alerts" data-testid="settings-alerts" className="block border border-ink/10 px-5 py-4 transition-colors hover:bg-sand">
                            Email & job-alert preferences →
                        </a>
                    </div>
                </section>

                <section>
                    <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-forest">
                        <span className="h-px w-6 bg-champagne" aria-hidden="true" />
                        Change password
                    </p>
                    <form data-testid="change-password-form" onSubmit={changePassword} className="mt-5 space-y-4">
                        <div>
                            <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-ink/55">Current password</span>
                            <input data-testid="current-password" type="password" required autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-ink/55">New password (min. 8 characters)</span>
                            <input data-testid="new-password" type="password" required minLength={8} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className={inputClass} />
                        </div>
                        {error && <p data-testid="password-error" className="border-l-2 border-champagne pl-4 text-sm text-ink/70">{error}</p>}
                        <div className="flex items-center gap-4">
                            <button
                                type="submit"
                                data-testid="change-password-btn"
                                disabled={busy}
                                className="inline-flex items-center gap-2 bg-forest px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:bg-forest-deep disabled:opacity-70"
                            >
                                {busy && <Loader2 size={14} className="animate-spin" />}
                                Update Password
                            </button>
                            {changed && (
                                <span data-testid="password-changed" className="inline-flex items-center gap-1.5 text-sm text-forest">
                                    <CheckCircle2 size={15} strokeWidth={1.5} />
                                    Password updated
                                </span>
                            )}
                        </div>
                    </form>
                </section>

                <section className="border border-ink/15 p-7">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-ink/50">
                        Deactivate account
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-ink/60">
                        Deactivating signs you out and disables access to your candidate portal. Your
                        applications remain with our recruitment team. This cannot be undone from the portal.
                    </p>
                    {!confirming ? (
                        <button
                            type="button"
                            data-testid="deactivate-start-btn"
                            onClick={() => setConfirming(true)}
                            className="mt-5 border border-ink/20 px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink/60 transition-colors hover:border-ink/50"
                        >
                            Deactivate my account
                        </button>
                    ) : (
                        <div className="mt-5 flex flex-wrap gap-3">
                            <button
                                type="button"
                                data-testid="deactivate-confirm-btn"
                                onClick={deactivate}
                                className="bg-ink px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:bg-night"
                            >
                                Yes, deactivate
                            </button>
                            <button
                                type="button"
                                data-testid="deactivate-cancel-btn"
                                onClick={() => setConfirming(false)}
                                className="border border-ink/20 px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ink/60"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </section>
            </div>
        </AccountLayout>
    );
}
